<script setup lang="ts">
import { computed } from 'vue';
import type { FieldSchema } from '@FormModule';

export interface AFieldButtonCheckboxOption {
  disabled?: boolean;
  disabledMessage?: string;
  icon?: string;
  label: string;
  labelTwo?: string;
  value: any;
}

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  centeredList?: boolean;
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  options?: AFieldButtonCheckboxOption[];
}>();

const model = defineModel();

const isCenteredList = computed(() => props.field?.edit?.centeredList || props.centeredList);
const disabledField = computed(() => props.field?.state.disabled || props.disabled);
const options = computed(() => props.field?.edit?.options || props.options);

const radioName = computed(() => props.field?.name || `button-checkbox-${Math.random()}`);

function getClasses(value: string, disabled: boolean) {
  return [
    'grid h-9 w-fit cursor-pointer items-center justify-center rounded-md border p-2 text-sm',
    'hover:text-white hover:!border :hover:!border-solid hover:border-brand-500 hover:bg-brand-600',
    { 'af-field-error': !!props.error },
    {
      'af-button-success text-white !border !border-solid border-brand-500': value === model.value,
      'opacity-50 pointer-events-none': disabled || disabledField.value,
      'w-full !justify-start px-2': isCenteredList.value,
    },
  ];
}
</script>

<template>
  <div :class="['flex flex-wrap items-center gap-2', { 'flex-col': isCenteredList }]">
    <div
      v-for="({ disabled, disabledMessage, icon, label, labelTwo, value }, index) in options"
      :key="`${label}_${index}`"
      :class="[
        'flex flex-wrap items-center gap-4',
        { 'w-full flex-nowrap items-start': isCenteredList },
      ]"
    >
      <i
        v-if="(disabled || disabledField) && disabledMessage"
        v-tooltip.top="$t(disabledMessage)"
        class="pi pi-info-circle cursor-help text-sm opacity-80"
      ></i>

      <label
        :class="getClasses(value, disabled)"
        :for="`${value}-${radioName}`"
      >
        <div class="flex cursor-pointer items-center gap-1">
          <FaIcon
            v-if="icon"
            :icon="$icons[icon]"
          />

          <span :class="[{ 'font-semibold': labelTwo }]">{{ $t(label) }}</span>
          <span v-if="labelTwo">{{ $t(labelTwo) }}</span>
        </div>

        <input
          v-model="model"
          class="hidden"
          :name="radioName"
          type="radio"
          :value
          :id="`${value}-${radioName}`"
        />
      </label>
    </div>
  </div>
</template>
